import { AxiosInstance, AxiosRequestConfig } from "axios";

import { AxiosInstanceInterceptorRequestExtensionI } from "@/api/interceptors/interface";

// Team → Forecast (C-16 S3, GLE-8410): /user/v1/manage/…/forecast. The
// server computes everything (common/pipelineforecast); the page draws it.
// Money is USD (float): GMV is the tier-aware customer charge, payout the
// booker's.

export interface ForecastMoneyI {
  gmv: number;
  payout: number;
}

/** A month-end forecast, where it comes from, and its P10–P90 range. */
export interface ForecastEstimateI {
  realized: number;
  pending: number;
  booked: number;
  not_yet_booked: number;
  /**
   * Realized QMs expected to drop by the month's final count (disputes and
   * disqualifications after the show mark), ≤ 0. Absent from older servers.
   */
  clawback?: number;
  forecast: number;
  p10: number;
  p90: number;
}

/** Meetings already set in a window and what they should bring. */
export interface ForecastBookedI {
  meetings: number;
  value: ForecastMoneyI;
  expected_qm: number;
  expected_value: ForecastMoneyI;
}

/** One full month of a group: QMs, target and QM money. */
export interface ForecastHistoryMonthI {
  month: string;
  qm: number;
  target: number;
  value: ForecastMoneyI;
}

export const FORECAST_SIGNALS = {
  DELIVERY: "delivery",
  RAMP: "ramp",
  THIS_MONTH: "this_month",
  MOMENTUM: "momentum",
  PIPELINE: "pipeline",
  ACTIVITY: "activity",
  FRICTION: "friction",
  NOTICE: "notice",
} as const;

export type ForecastSignalKeyI =
  (typeof FORECAST_SIGNALS)[keyof typeof FORECAST_SIGNALS];

export const FORECAST_RISK_BANDS = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  NOTICE: "notice",
} as const;

export type ForecastRiskBandI =
  (typeof FORECAST_RISK_BANDS)[keyof typeof FORECAST_RISK_BANDS];

/** One input to the churn score; `measure` is null when unknown (it scores 0). */
export interface ForecastSignalI {
  key: ForecastSignalKeyI;
  weight: number;
  value: number;
  points: number;
  measure: number | null;
}

export interface ForecastRiskI {
  score: number;
  band: ForecastRiskBandI;
  signals: Array<ForecastSignalI>;
  /** The (at most two) signals that add the most. */
  reasons: Array<ForecastSignalKeyI>;
  /** Mean monthly QM money of the last 3 full months. */
  at_stake: ForecastMoneyI;
  /** score ÷ 100 × GMV at stake: the risk list's order. */
  exposure: number;
}

/** One campaign group's month (an ungrouped campaign is its own group). */
export interface ForecastGroupI {
  key: string;
  name: string;
  workspace_id: string;
  is_group: boolean;
  is_active: boolean;
  target: number;
  qm: ForecastEstimateI;
  gmv: ForecastEstimateI;
  payout: ForecastEstimateI;
  /** Forecast ÷ target; null without a target. */
  fulfillment: number | null;
  /** P(QMs ≥ target); null without a target. */
  chance: number | null;
  /** Σ QM ÷ Σ target over the last 3 full months with a target. */
  historic_fulfillment: number | null;
  above_recent_best: boolean;
  needed_bookings_per_day: number | null;
  bookings_per_day: number;
  qual_rate: number;
  cancel_rate: number;
  lead_days: number;
  booked_next_14_days: ForecastBookedI;
  /** The last 6 full months, oldest first. */
  history: Array<ForecastHistoryMonthI>;
  /** An active campaign group's churn risk, as of today; null otherwise. */
  risk: ForecastRiskI | null;
}

/** The scope's month. Fulfillment, chance and the historic fulfillment
 * count only groups with a target (like with like). */
export interface ForecastTotalsI {
  target: number;
  qm: ForecastEstimateI;
  gmv: ForecastEstimateI;
  payout: ForecastEstimateI;
  targeted_forecast: number;
  /**
   * The QMs of the campaigns with a target, the side a target is set
   * against (C16-D448); null without a target. Optional until the backend
   * sends it.
   */
  targeted_qm?: ForecastEstimateI | null;
  fulfillment: number | null;
  chance: number | null;
  historic_fulfillment: number | null;
  booked_next_14_days: ForecastBookedI;
  groups: number;
  targeted_groups: number;
  risk_notice: number;
  risk_high: number;
  risk_medium: number;
}

/** One business day of the month-landing chart. */
export interface ForecastLandingPointI {
  date: string;
  actual: number | null;
  forecast: number | null;
  p10: number | null;
  p90: number | null;
  target_pace: number | null;
  last_month: number | null;
}

export interface ForecastWeekSegmentI {
  /** A group's key, or (All workspaces) a workspace id, unnamed. */
  key: string;
  name: string;
  booked: number;
  expected_qm: number;
}

export interface ForecastWeekI {
  week_start: string;
  booked: number;
  booked_value: ForecastMoneyI;
  expected_qm: number;
  expected_value: ForecastMoneyI;
  pending: number;
  pending_expected_qm: number;
  segments: Array<ForecastWeekSegmentI>;
}

export interface ForecastGroupRiskI {
  key: string;
  name: string;
  workspace_id: string;
  risk: ForecastRiskI;
}

export interface ForecastResponseI {
  /** When the cards were read: the forecast is as of then. */
  as_of: string;
  month: string;
  is_current: boolean;
  business_days: number;
  business_days_elapsed: number;
  /** This month's meetings still waiting for feedback after 14 days. */
  stale_pending: number;
  totals: ForecastTotalsI;
  groups: Array<ForecastGroupI>;
  /** Every live campaign group's churn risk, as of today, by exposure. */
  risks?: Array<ForecastGroupRiskI>;
  landing: Array<ForecastLandingPointI>;
  /** The server always sends a list; the page reads a missing one as empty. */
  weeks: Array<ForecastWeekI> | null;
}

export const FORECAST_MONTHS = {
  CURRENT: "current",
  NEXT: "next",
} as const;

export type ForecastMonthI =
  (typeof FORECAST_MONTHS)[keyof typeof FORECAST_MONTHS];

/** A 404 (+ 1035) means "switched off": an answer the page reads. */
const switchProbe = (
  config: AxiosRequestConfig = {}
): AxiosRequestConfig & AxiosInstanceInterceptorRequestExtensionI => ({
  ...config,
  meta: { expectedStatuses: [404] },
});

export const ManageForecastAPIEndpoints = (api: AxiosInstance) => ({
  /** A workspace's month, or (null) every workspace's (Admins). */
  getForecast: (
    workspaceId: string | null,
    month: ForecastMonthI,
    excludeHomefix: boolean
  ) =>
    api.get<ForecastResponseI>(
      workspaceId
        ? `/v1/manage/workspace/${encodeURIComponent(workspaceId)}/forecast`
        : "/v1/manage/forecast",
      switchProbe({ params: { month, exclude_homefix: excludeHomefix } })
    ),
});
